import React from 'react';
import { motion } from 'framer-motion';
import { Award, Briefcase, Code } from 'lucide-react';
import { personalInfo, education, certifications } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="about" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div variants={itemVariants} className="inline-block">
              <Badge variant="outline" className="mb-4">
                <Code className="w-3 h-3 mr-1" />
                About Me
              </Badge>
            </motion.div>
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-bold mb-4"
            >
              Who I Am
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              Passionate about building scalable solutions and solving complex problems
            </motion.p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Bio Section */}
            <motion.div variants={itemVariants}>
              <Card className="h-full">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <Briefcase className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="text-2xl font-bold">Professional Journey</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {personalInfo.bio}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span>Based in {personalInfo.location}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Education & Certifications */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Education */}
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 rounded-lg bg-emerald-500/10">
                      <Award className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h3 className="text-2xl font-bold">Education</h3>
                  </div>
                  <div className="space-y-4">
                    {education.slice(0, 2).map((edu) => (
                      <div key={edu.id} className="border-l-2 border-border pl-4">
                        <h4 className="font-semibold text-sm">{edu.degree}</h4>
                        <p className="text-sm text-muted-foreground">{edu.institution}</p>
                        <p className="text-xs text-muted-foreground">{edu.period}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card>
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold mb-4">Key Certifications</h3>
                  <div className="space-y-2">
                    {certifications.slice(0, 5).map((cert, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2" />
                        <p className="text-sm text-muted-foreground">{cert}</p>
                      </div>
                    ))}
                    {certifications.length > 5 && (
                      <p className="text-xs text-muted-foreground italic pt-2">
                        +{certifications.length - 5} more certifications
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
